import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, PackageCheck, PackageX, ShieldCheck, TriangleAlert } from "lucide-react";
import { getOrderStatus } from "@/lib/orders.functions";
import { SectionHeading } from "@/components/SectionHeading";

export const Route = createFileRoute("/suivi/$orderNumber")({
  head: ({ params }) => {
    const num = params.orderNumber.replace(/^#?/, "#");
    const title = `Suivi de commande ${num} — Sunset Concept Store`;
    const description = `Suivez l'état de votre commande ${num} : préparation, livraison et paiement à la livraison.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "noindex, nofollow" },
        { property: "og:type", content: "website" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
    };
  },
  component: SuiviPage,
});

type StatusResult = Awaited<ReturnType<typeof getOrderStatus>>;
type OrderInfo = Extract<StatusResult, { found: true }>["order"];

const STATUS_LABELS: Record<string, string> = {
  created: "Commande confirmée",
  cancelled: "Commande annulée",
};

const FULFILLMENT_LABELS: Record<string, string> = {
  UNFULFILLED: "En préparation",
  IN_PROGRESS: "Préparation en cours",
  PARTIALLY_FULFILLED: "Partiellement expédiée",
  FULFILLED: "Expédiée",
  ON_HOLD: "En attente",
  SCHEDULED: "Expédition planifiée",
};

const FINANCIAL_LABELS: Record<string, string> = {
  PENDING: "Paiement à la livraison",
  PAID: "Payée",
  REFUNDED: "Remboursée",
  PARTIALLY_REFUNDED: "Partiellement remboursée",
  VOIDED: "Annulée",
};

function label(map: Record<string, string>, value: string | null, fallback: string) {
  if (!value) return fallback;
  return map[value.toUpperCase()] ?? value;
}

function SuiviPage() {
  const { orderNumber } = Route.useParams();
  const fetchStatus = useServerFn(getOrderStatus);

  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<OrderInfo | null>(null);
  const [notFound, setNotFound] = useState(false);

  const display = orderNumber.replace(/^#?/, "#");

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setOrder(null);
    setNotFound(false);
    try {
      const res = await fetchStatus({ data: { orderNumber, phone } });
      if (res.found) setOrder(res.order);
      else setNotFound(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? "Impossible de vérifier votre commande pour le moment. Réessayez dans un instant."
          : "Erreur inattendue.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="mx-auto min-h-[70vh] max-w-2xl px-6 py-24 md:py-32">
      <SectionHeading
        eyebrow="Suivi"
        title={`Commande ${display}`}
        subtitle="Saisissez le numéro de téléphone utilisé lors de la commande pour afficher son statut vérifié."
      />

      <form onSubmit={onSubmit} className="mt-12 space-y-4">
        <label htmlFor="phone" className="label-xs block text-muted-foreground">
          Téléphone de la commande
        </label>
        <input
          id="phone"
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="06 12 34 56 78"
          className="h-14 w-full rounded-sm border border-border bg-card px-4 text-base outline-none transition-colors focus:border-foreground"
        />
        <button
          type="submit"
          disabled={loading || phone.trim().length < 6}
          className="label-xs flex h-14 w-full items-center justify-center gap-2 border border-foreground px-8 transition-colors hover:bg-primary hover:text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : null}
          {loading ? "Vérification en cours" : "Vérifier ma commande"}
        </button>
      </form>

      <div className="mt-10 min-h-[10rem]" aria-live="polite">
        {error ? (
          <p className="flex items-center gap-2 rounded-sm border border-border bg-card/60 p-5 text-sm text-muted-foreground">
            <TriangleAlert size={16} strokeWidth={1.4} /> {error}
          </p>
        ) : null}

        {notFound ? (
          <p className="flex items-center gap-2 rounded-sm border border-border bg-card/60 p-5 text-sm text-muted-foreground">
            <PackageX size={16} strokeWidth={1.4} /> Aucune commande {display} ne correspond à ce
            numéro de téléphone.
          </p>
        ) : null}

        {order ? (
          <div className="rounded-sm border border-border bg-card/60 p-6">
            <div className="flex items-center gap-2">
              <PackageCheck size={18} strokeWidth={1.4} />
              <p className="font-serif text-2xl">{order.orderNumber}</p>
            </div>

            <dl className="mt-6 space-y-4 text-sm">
              <div className="flex items-baseline justify-between gap-4">
                <dt className="label-xs text-muted-foreground">Statut</dt>
                <dd>{label(STATUS_LABELS, order.status, order.status)}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <dt className="label-xs text-muted-foreground">Livraison</dt>
                <dd>{label(FULFILLMENT_LABELS, order.fulfillmentStatus, "En préparation")}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <dt className="label-xs text-muted-foreground">Paiement</dt>
                <dd>
                  {label(FINANCIAL_LABELS, order.financialStatus, "Paiement à la livraison")}
                </dd>
              </div>
              {order.totalAmount !== null ? (
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="label-xs text-muted-foreground">Total</dt>
                  <dd>
                    {order.totalAmount} {order.currency === "MAD" ? "DH" : (order.currency ?? "")}
                  </dd>
                </div>
              ) : null}
              <div className="flex items-baseline justify-between gap-4">
                <dt className="label-xs text-muted-foreground">Commandée le</dt>
                <dd>{new Date(order.createdAt).toLocaleDateString("fr-FR")}</dd>
              </div>
              {order.cancelledAt ? (
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="label-xs text-muted-foreground">Annulée le</dt>
                  <dd>{new Date(order.cancelledAt).toLocaleDateString("fr-FR")}</dd>
                </div>
              ) : null}
            </dl>

            <p className="mt-6 flex items-center gap-2 border-t border-border pt-4 text-xs text-muted-foreground">
              {order.verified ? (
                <>
                  <ShieldCheck size={14} strokeWidth={1.4} /> Statut vérifié en direct auprès de
                  notre système de commandes.
                </>
              ) : (
                <>
                  <TriangleAlert size={14} strokeWidth={1.4} /> Vérification en direct
                  indisponible : dernier statut connu affiché.
                </>
              )}
            </p>
          </div>
        ) : null}
      </div>
    </main>
  );
}
